import { formatDateKey } from "@teamna/shared";
import { Button } from "../../components/index.ts";
import { Chips } from "./Rail.tsx";

/**
 * The rail of a month with no sessions yet (spec `teamna/strategy/timetable-months.md`, prototype
 * `timetable-flow-v2.html` scene 2): start from a copy of the month before, or start empty. The week
 * shows the month before as dashed blocks meanwhile, so the owner sees what a copy brings in.
 */
export function MonthStart({
  words,
  monthName,
  previousName,
  weeklyTimes,
  levels,
  sessions,
  onCopy,
  onEmpty,
}: {
  /** The business's words: "families" or "clients", "level". */
  words: { families: string; group: string };
  /** "December" */
  monthName: string;
  /** "November" */
  previousName: string;
  weeklyTimes: number;
  /** Each level of the month before with its weekly times: "Dolphins 3". */
  levels: Array<{ id: string; name: string; color: string; weekly: number }>;
  /** The sessions a copy gives in the month. */
  sessions: number;
  onCopy: () => void;
  onEmpty: () => void;
}) {
  return (
    <div className="grid gap-3">
      <div className="grid gap-1">
        <h2 className="text-[16px] font-bold">{monthName} has no sessions yet</h2>
        <p className="text-[12.5px] text-ink-2">
          {words.families.charAt(0).toUpperCase()}
          {words.families.slice(1)} can book {monthName} after you publish it. How do you want to start?
        </p>
      </div>
      <div className="grid gap-2 rounded-xl border-[1.5px] border-accent bg-accent-surface p-2.5">
        <b className="text-[14px]">Copy {previousName}</b>
        <p className="text-[12px] text-ink-2">
          The dashed times on the week. {weeklyTimes} weekly {weeklyTimes === 1 ? "time" : "times"}, {sessions} {sessions === 1 ? "session" : "sessions"} in {monthName}. You
          can change them before you publish.
        </p>
        {levels.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {levels.map((level) => (
              <span key={level.id} className="inline-flex items-center gap-1 rounded-md bg-paper px-1.5 py-0.5 text-[11px] font-bold text-ink-2 tabular-nums">
                <i className="size-2 rounded-[2px]" style={{ background: level.color }} aria-hidden="true" />
                {level.name} {level.weekly}
              </span>
            ))}
          </div>
        ) : null}
        <Button fullWidth onClick={onCopy}>
          Copy {previousName}
        </Button>
      </div>
      <div className="grid gap-2 rounded-xl border border-line p-2.5">
        <b className="text-[14px]">Start empty</b>
        <p className="text-[12px] text-ink-2">
          Add each time again, {words.group} by {words.group}.
        </p>
        <Button variant="secondary" fullWidth onClick={onEmpty}>
          Start empty
        </Button>
      </div>
    </div>
  );
}

/**
 * After a copy, before the publish: the fixed UAE holidays of the month (closed by default, the owner
 * can open each one) and the count of sessions.
 */
export function MonthCheck({
  monthName,
  previousName,
  holidays,
  sessions,
  onHoliday,
}: {
  monthName: string;
  previousName: string;
  holidays: Array<{ date: string; reason: string; closed: boolean }>;
  sessions: number;
  onHoliday: (date: string, closed: boolean) => void;
}) {
  const closed = holidays.filter((holiday) => holiday.closed).length;
  return (
    <div className="grid gap-2 rounded-xl border border-line bg-sunken p-2.5">
      <b className="text-[13px]">Check before you publish</b>
      <p className="text-[12px] text-ink-2">
        Copied from {previousName}. {sessions} {sessions === 1 ? "session" : "sessions"} in {monthName}. Change any time on the week.
      </p>
      {holidays.length > 0 ? (
        <div className="grid gap-1.5">
          <span className="text-[11.5px] font-bold text-ink-2">
            {holidays.length} UAE public {holidays.length === 1 ? "holiday" : "holidays"}. {closed === 0 ? "All open." : `${closed} closed: no sessions on ${closed === 1 ? "it" : "them"}.`}
          </span>
          {holidays.map((holiday) => (
            <div key={holiday.date} className="flex items-center justify-between gap-2 text-[12px]">
              <span className="min-w-0">
                {formatDateKey(holiday.date)}, {holiday.reason}
              </span>
              <Chips
                label={`${formatDateKey(holiday.date)}, ${holiday.reason}`}
                value={holiday.closed ? "closed" : "open"}
                options={[
                  { value: "closed", label: "Closed" },
                  { value: "open", label: "Open" },
                ]}
                onChange={(value) => onHoliday(holiday.date, value === "closed")}
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
